import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { salesService, viaCepService, usersService, apiConfigService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  maskCPF, maskCNPJ, maskPhone, maskCEP, maskMoney, unmaskMoney,
  validateCPF, validateCNPJ 
} from '../utils/masks';
import '../styles/NovaVenda.css';

const NovaVenda = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [buscandoCpf, setBuscandoCpf] = useState(false);
  const cpfBuscadoRef = useRef('');

  // Seletor de vendedor (apenas supervisor): ele + subordinados
  const [vendedorId, setVendedorId] = useState(null);
  const [opcoesVendedor, setOpcoesVendedor] = useState([]);
  
  // Estado do formulário
  const [formData, setFormData] = useState({
    tipo_cliente: 'cpf',
    documento: '',
    razao_social: '',
    estado_cnpj: '',
    nome_cliente: '',
    telefone: '',
    telefone2: '',
    email: '',
    cpf: '',
    nome_representante: '',
    rg: '',
    nome_mae: '',
    data_nascimento: '',
    cep: '',
    logradouro: '',
    bairro: '',
    cidade: '',
    estado: '',
    estado_completo: '',
    numero: '',
    complemento1: '',
    complemento2: '',
    data_agendamento1: '',
    periodo_agendamento1: 'manha',
    data_agendamento2: '',
    periodo_agendamento2: '',
    aceita_antecipacao: false,
    observacao_instalacao: '',
    data_vencimento: 10,
    valor_total: '',
    observacao_venda: ''
  });

  const [errors, setErrors] = useState({});
  const [operadoraSelecionada, setOperadoraSelecionada] = useState(null);
  const [operadoraViabilidadeJson, setOperadoraViabilidadeJson] = useState(null);
  const [documentosAnexos, setDocumentosAnexos] = useState([]);
  const [showModalPacote, setShowModalPacote] = useState(false);
  const observacaoVendaRef = useRef(null);

  // Receber dados da consulta de viabilidade se existirem
  useEffect(() => {
    if (location.state) {
      const { cep, numero, operadora_selecionada, operadora_viabilidade_json } = location.state;
      
      if (cep) {
        setFormData(prev => ({
          ...prev,
          cep: maskCEP(cep)
        }));
      }
      
      if (numero) {
        setFormData(prev => ({
          ...prev,
          numero: numero
        }));
      }
      
      if (operadora_selecionada) {
        setOperadoraSelecionada(operadora_selecionada);
      }
      
      if (operadora_viabilidade_json) {
        setOperadoraViabilidadeJson(operadora_viabilidade_json);
      }
    }
  }, [location.state]);

  // Buscar dados por CPF quando documento (PF) ou cpf (PJ) tiver 11 dígitos válidos
  useEffect(() => {
    const tipoCliente = formData.tipo_cliente;
    const buscarPorCpf = async (cpfLimpo) => {
      if (cpfBuscadoRef.current === cpfLimpo) return;
      if (!validateCPF(maskCPF(cpfLimpo))) return;

      try {
        setBuscandoCpf(true);
        cpfBuscadoRef.current = cpfLimpo;
        const data = await apiConfigService.buscarCpf(cpfLimpo);

        const formatarData = (dt) => {
          if (!dt || dt === 'SEM RESULTADO') return '';
          const parts = String(dt).replace(/\\\//g, '/').split('/');
          if (parts.length === 3) {
            const [d, m, y] = parts;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
          }
          return '';
        };

        const tel = data.TELEFONES || '';
        const telFormatado = tel ? maskPhone(tel.replace(/\D/g, '').slice(0, 11)) : '';

        setFormData(prev => {
          const updates = {
            data_nascimento: formatarData(data.DT_NASCIMENTO) || prev.data_nascimento,
            nome_mae: data.NOME_MAE || prev.nome_mae,
            telefone: telFormatado || prev.telefone,
            email: (data.EMAIL && data.EMAIL !== 'SEM RESULTADO') ? data.EMAIL : prev.email
          };
          if (tipoCliente === 'cpf') {
            updates.nome_cliente = data.NOME || prev.nome_cliente;
          } else if (tipoCliente === 'cnpj') {
            // Quando for CNPJ, preenche o nome do representante
            updates.nome_representante = data.NOME || prev.nome_representante;
          }
          return { ...prev, ...updates };
        });
      } catch (err) {
        console.error('Erro ao buscar CPF:', err);
        cpfBuscadoRef.current = '';
      } finally {
        setBuscandoCpf(false);
      }
    };

    if (formData.tipo_cliente === 'cpf') {
      const doc = formData.documento.replace(/\D/g, '');
      if (doc.length === 11) {
        buscarPorCpf(doc);
      } else {
        cpfBuscadoRef.current = '';
      }
    } else if (formData.tipo_cliente === 'cnpj') {
      const cpfDoc = formData.cpf.replace(/\D/g, '');
      if (cpfDoc.length === 11) {
        buscarPorCpf(cpfDoc);
      } else {
        cpfBuscadoRef.current = '';
      }
    }
  }, [formData.tipo_cliente, formData.documento, formData.cpf]);

  // Carregar "Eu (Supervisor/Coordenador)" + subordinados quando for supervisor ou coordenador
  useEffect(() => {
    if (user?.role !== 'supervisor' && user?.role !== 'coordenador') return;
    const load = async () => {
      const titulo = user?.role === 'coordenador' ? 'Coordenador' : 'Supervisor';
      const eu = { value: user.id, label: `Eu (${user.nome || user.username || titulo})` };
      setOpcoesVendedor([eu]);
      setVendedorId(user.id);
      try {
        const subordinados = await usersService.getVendedoresBySupervisor(user.id);
        const extras = (subordinados || []).map(s => ({
          value: s.id,
          label: s.nome || s.username || `ID ${s.id}`
        }));
        setOpcoesVendedor([eu, ...extras]);
      } catch (err) {
        console.error('Erro ao carregar subordinados:', err);
      }
    };
    load();
  }, [user?.id, user?.role, user?.nome, user?.username]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Aplicar máscaras
    if (name === 'documento') {
      newValue = formData.tipo_cliente === 'cpf' ? maskCPF(value) : maskCNPJ(value);
      
      // Validação automática para CPF
      if (formData.tipo_cliente === 'cpf' && newValue.replace(/\D/g, '').length === 11) {
        if (validateCPF(newValue)) {
          setErrors(prev => ({ ...prev, documento: '' }));
        } else {
          setErrors(prev => ({ ...prev, documento: 'CPF inválido' }));
        }
      }
    } else if (name === 'telefone' || name === 'telefone2') {
      newValue = maskPhone(value);
    } else if (name === 'cpf') {
      newValue = maskCPF(value);
      // Validação automática do CPF quando preenchido
      if (newValue.replace(/\D/g, '').length === 11) {
        if (validateCPF(newValue)) {
          setErrors(prev => ({ ...prev, cpf: '' }));
        } else {
          setErrors(prev => ({ ...prev, cpf: 'CPF inválido' }));
        }
      }
    } else if (name === 'cep') {
      newValue = maskCEP(value);
    } else if (name === 'valor_total') {
      newValue = maskMoney(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Limpar erro do campo
    if (errors[name] && name !== 'documento' && name !== 'cpf') {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTipoClienteChange = (tipo) => {
    setFormData(prev => ({
      ...prev,
      tipo_cliente: tipo,
      documento: '',
      // Limpar campos específicos do CNPJ quando mudar para CPF
      ...(tipo === 'cpf' ? {
        razao_social: '',
        estado_cnpj: '',
        telefone2: '',
        email: '',
        cpf: '',
        nome_representante: '',
        rg: '',
        nome_mae: '',
        data_nascimento: ''
      } : {
        // Limpar campos específicos do CPF quando mudar para CNPJ
        nome_cliente: ''
      })
    }));
    setErrors(prev => ({ ...prev, documento: '' }));
  };

  const buscarCep = async () => {
    const cepLimpo = formData.cep.replace(/\D/g, '');
    
    if (cepLimpo.length !== 8) {
      setErrors(prev => ({ ...prev, cep: 'CEP inválido' }));
      return;
    }

    try {
      setBuscandoCep(true);
      const data = await viaCepService.buscarCep(cepLimpo);
      
      setFormData(prev => ({
        ...prev,
        logradouro: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
        estado_completo: data.estado || ''
      }));
      
      setErrors(prev => ({ ...prev, cep: '' }));
    } catch (error) {
      setErrors(prev => ({ ...prev, cep: 'CEP não encontrado' }));
    } finally {
      setBuscandoCep(false);
    }
  };

  const buscarCnpj = async () => {
    const cnpjLimpo = formData.documento.replace(/\D/g, '');
    
    if (cnpjLimpo.length !== 14) {
      setErrors(prev => ({ ...prev, documento: 'CNPJ inválido' }));
      return;
    }

    if (!validateCNPJ(formData.documento)) {
      setErrors(prev => ({ ...prev, documento: 'CNPJ inválido' }));
      return;
    }

    try {
      setBuscandoCnpj(true);
      const data = await salesService.buscarCnpj(cnpjLimpo);
      
      const razaoSocial = data.nome || '';
      setFormData(prev => ({
        ...prev,
        nome_cliente: razaoSocial,
        razao_social: razaoSocial,
        estado_cnpj: data.situacao || '',
      }));
      
      setErrors(prev => ({ ...prev, documento: '' }));
      alert('✅ CNPJ encontrado! Razão Social preenchida automaticamente.');
    } catch (error) {
      console.error('Erro ao buscar CNPJ:', error);
      setErrors(prev => ({ ...prev, documento: error.response?.data?.error || 'CNPJ não encontrado ou inválido' }));
    } finally {
      setBuscandoCnpj(false);
    }
  };


  const validateForm = () => {
    const newErrors = {};

    // Validação de documento
    if (!formData.documento) {
      newErrors.documento = 'Documento é obrigatório';
    } else if (formData.tipo_cliente === 'cpf' && !validateCPF(formData.documento)) {
      newErrors.documento = 'CPF inválido';
    } else if (formData.tipo_cliente === 'cnpj' && !validateCNPJ(formData.documento)) {
      newErrors.documento = 'CNPJ inválido';
    }

    // Validações obrigatórias
    const requiredFields = {
      telefone: 'Telefone',
      cep: 'CEP',
      logradouro: 'Logradouro',
      bairro: 'Bairro',
      cidade: 'Cidade',
      estado: 'Estado',
      estado_completo: 'Estado completo',
      numero: 'Número',
      data_agendamento1: 'Data de agendamento 1',
      valor_total: 'Valor total'
    };

    // Validação de nome_cliente ou razao_social
    if (formData.tipo_cliente === 'cpf') {
      if (!formData.nome_cliente) {
        newErrors.nome_cliente = 'Nome do cliente é obrigatório';
      }
    }

    // Validação de email (obrigatório para ambos)
    if (!formData.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Validações específicas para CPF
    if (formData.tipo_cliente === 'cpf') {
      if (!formData.rg) {
        newErrors.rg = 'RG é obrigatório';
      }
      if (!formData.nome_mae) {
        newErrors.nome_mae = 'Nome da mãe é obrigatório';
      }
      if (!formData.data_nascimento) {
        newErrors.data_nascimento = 'Data de nascimento é obrigatória';
      }
    }

    // Validações específicas para CNPJ
    if (formData.tipo_cliente === 'cnpj') {
      if (!formData.razao_social) {
        newErrors.razao_social = 'Razão Social é obrigatória';
      }
      if (!formData.estado_cnpj) {
        newErrors.estado_cnpj = 'Estado do CNPJ é obrigatório';
      }
      if (!formData.cpf) {
        newErrors.cpf = 'CPF é obrigatório';
      } else if (!validateCPF(formData.cpf)) {
        newErrors.cpf = 'CPF inválido';
      }
      if (!formData.nome_representante) {
        newErrors.nome_representante = 'Nome do representante é obrigatório';
      }
      if (!formData.rg) {
        newErrors.rg = 'RG é obrigatório';
      }
      if (!formData.nome_mae) {
        newErrors.nome_mae = 'Nome da mãe é obrigatório';
      }
      if (!formData.data_nascimento) {
        newErrors.data_nascimento = 'Data de nascimento é obrigatória';
      }
    }

    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!formData[field]) {
        newErrors[field] = `${label} é obrigatório`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const confirmarEnvioVenda = async () => {
    try {
      setLoading(true);

      // Preparar dados da venda
      const saleData = {
        ...formData,
        documento: formData.documento.replace(/\D/g, ''),
        nome_cliente: formData.tipo_cliente === 'cnpj' ? formData.razao_social : formData.nome_cliente,
        telefone: formData.telefone.replace(/\D/g, ''),
        telefone2: formData.telefone2 ? formData.telefone2.replace(/\D/g, '') : null,
        cpf: formData.cpf ? formData.cpf.replace(/\D/g, '') : null,
        cep: formData.cep.replace(/\D/g, ''),
        valor_total: unmaskMoney(formData.valor_total),
        data_agendamento2: formData.data_agendamento2 || null,
        periodo_agendamento2: formData.periodo_agendamento2 || null,
        data_nascimento: formData.data_nascimento || null,
        operadora_selecionada: operadoraSelecionada || null,
        operadora_viabilidade_json: operadoraViabilidadeJson ? JSON.stringify(operadoraViabilidadeJson) : null
      };

      if ((user?.role === 'supervisor' || user?.role === 'coordenador') && vendedorId != null) {
        saleData.vendedor_id = vendedorId;
      }

      // Se houver arquivos anexos, usar FormData
      if (documentosAnexos.length > 0) {
        const formDataToSend = new FormData();
        
        // Adicionar todos os campos como string
        Object.keys(saleData).forEach(key => {
          const value = saleData[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object') {
              formDataToSend.append(key, JSON.stringify(value));
            } else if (typeof value === 'boolean') {
              formDataToSend.append(key, value.toString());
            } else {
              formDataToSend.append(key, value);
            }
          }
        });
        
        // Adicionar todos os arquivos
        documentosAnexos.forEach((file) => {
          formDataToSend.append('documento_anexo', file);
        });
        
        await salesService.create(formDataToSend, true); // true indica FormData
      } else {
        await salesService.create(saleData);
      }
      
      alert('✅ Venda cadastrada com sucesso!');
      navigate('/vendas');
    } catch (error) {
      console.error('Erro ao criar venda:', error);
      alert('❌ Erro ao cadastrar venda: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Por favor, preencha todos os campos obrigatórios corretamente');
      return;
    }

    setShowModalPacote(true);
  };

  return (
    <Layout>
      <div className="nova-venda-page">
        <div className="page-header">
          <h1>➕ Nova Venda</h1>
          <p>Cadastre uma nova venda no sistema</p>
        </div>

        <form onSubmit={handleSubmit} className="venda-form">
          {/* Operadora Selecionada (se vier da consulta de viabilidade) */}
          {operadoraSelecionada && (
            <div className="form-section operadora-info">
              <h2>📡 Operadora Selecionada</h2>
              <div className="operadora-selecionada-info">
                <strong>{operadoraSelecionada.charAt(0).toUpperCase() + operadoraSelecionada.slice(1)}</strong>
                <span className="info-text">Operadora selecionada na consulta de viabilidade</span>
              </div>
            </div>
          )}

          {/* Registrar venda para (supervisor ou coordenador) */}
          {(user?.role === 'supervisor' || user?.role === 'coordenador') && opcoesVendedor.length > 0 && (
            <div className="form-section">
              <h2>👤 Registrar venda para</h2>
              <div className="form-group">
                <label htmlFor="vendedor-select">Vendedor</label>
                <select
                  id="vendedor-select"
                  value={vendedorId ?? ''}
                  onChange={(e) => setVendedorId(parseInt(e.target.value, 10))}
                >
                  {opcoesVendedor.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Tipo de Cliente */}
          <div className="form-section">
            <h2>👤 Tipo de Cliente</h2>
            <div className="tipo-cliente-buttons">
              <button
                type="button"
                className={`tipo-btn ${formData.tipo_cliente === 'cpf' ? 'active' : ''}`}
                onClick={() => handleTipoClienteChange('cpf')}
              >
                👤 Pessoa Física (CPF)
              </button>
              <button
                type="button"
                className={`tipo-btn ${formData.tipo_cliente === 'cnpj' ? 'active' : ''}`}
                onClick={() => handleTipoClienteChange('cnpj')}
              >
                🏢 Pessoa Jurídica (CNPJ)
              </button>
            </div>
          </div>

          {/* Dados do Cliente */}
          <div className="form-section">
            <h2>👤 Dados do Cliente</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  {formData.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'} *
                </label>
                {formData.tipo_cliente === 'cnpj' ? (
                  <div className="cep-input-wrapper">
                    <input
                      type="text"
                      name="documento"
                      value={formData.documento}
                      onChange={handleChange}
                      placeholder="00.000.000/0000-00"
                      className={errors.documento ? 'error' : ''}
                    />
                    <button
                      type="button"
                      onClick={buscarCnpj}
                      className="btn-buscar-cep"
                      disabled={buscandoCnpj}
                    >
                      {buscandoCnpj ? '⏳' : '🔍'} Buscar
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    name="documento"
                    value={formData.documento}
                    onChange={handleChange}
                    placeholder="000.000.000-00"
                    className={errors.documento ? 'error' : ''}
                  />
                )}
                {errors.documento && <span className="error-text">{errors.documento}</span>}
                {formData.tipo_cliente === 'cpf' && formData.documento.replace(/\D/g, '').length === 11 && !errors.documento && (
                  <span className="success-text" style={{ color: '#28a745', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {buscandoCpf ? '⏳ Buscando dados...' : '✅ CPF válido'}
                  </span>
                )}
              </div>

              {formData.tipo_cliente === 'cnpj' ? (
                <>
                  <div className="form-group">
                    <label>Razão Social * <span className="auto-fill-badge">Preenchido automaticamente</span></label>
                    <textarea
                      name="razao_social"
                      value={formData.razao_social}
                      onChange={handleChange}
                      placeholder="Preenchido automaticamente"
                      className={`readonly-input ${errors.razao_social ? 'error' : ''}`}
                      readOnly
                      rows={2}
                      title={formData.razao_social}
                    />
                    {errors.razao_social && <span className="error-text">{errors.razao_social}</span>}
                  </div>

                  <div className="form-group">
                    <label>Estado do CNPJ * <span className="auto-fill-badge">Preenchido automaticamente</span></label>
                    <input
                      type="text"
                      name="estado_cnpj"
                      value={formData.estado_cnpj}
                      onChange={handleChange}
                      placeholder="Preenchido automaticamente"
                      className={`readonly-input ${errors.estado_cnpj ? 'error' : ''}`}
                      readOnly
                    />
                    {errors.estado_cnpj && <span className="error-text">{errors.estado_cnpj}</span>}
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    name="nome_cliente"
                    value={formData.nome_cliente}
                    onChange={handleChange}
                    placeholder="Nome completo do cliente"
                    className={errors.nome_cliente ? 'error' : ''}
                    disabled={buscandoCpf}
                  />
                  {errors.nome_cliente && <span className="error-text">{errors.nome_cliente}</span>}
                </div>
              )}

              <div className="form-group">
                <label>Telefone 1 *</label>
                <input
                  type="text"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  className={errors.telefone ? 'error' : ''}
                  disabled={buscandoCpf}
                />
                {errors.telefone && <span className="error-text">{errors.telefone}</span>}
              </div>

              <div className="form-group">
                <label>Telefone 2</label>
                <input
                  type="text"
                  name="telefone2"
                  value={formData.telefone2}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  className={errors.telefone2 ? 'error' : ''}
                />
                {errors.telefone2 && <span className="error-text">{errors.telefone2}</span>}
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@exemplo.com"
                  className={errors.email ? 'error' : ''}
                  disabled={buscandoCpf}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              {/* Campos adicionais para CPF */}
              {formData.tipo_cliente === 'cpf' && (
                <>
                  <div className="form-group">
                    <label>RG *</label>
                    <input
                      type="text"
                      name="rg"
                      value={formData.rg}
                      onChange={handleChange}
                      placeholder="00.000.000-0"
                      className={errors.rg ? 'error' : ''}
                    />
                    {errors.rg && <span className="error-text">{errors.rg}</span>}
                  </div>

                  <div className="form-group">
                    <label>Nome da Mãe *</label>
                    <input
                      type="text"
                      name="nome_mae"
                      value={formData.nome_mae}
                      onChange={handleChange}
                      placeholder="Nome completo da mãe"
                      className={errors.nome_mae ? 'error' : ''}
                      disabled={buscandoCpf}
                    />
                    {errors.nome_mae && <span className="error-text">{errors.nome_mae}</span>}
                  </div>

                  <div className="form-group">
                    <label>Data de Nascimento *</label>
                    <input
                      type="date"
                      name="data_nascimento"
                      value={formData.data_nascimento}
                      onChange={handleChange}
                      className={errors.data_nascimento ? 'error' : ''}
                      disabled={buscandoCpf}
                    />
                    {errors.data_nascimento && <span className="error-text">{errors.data_nascimento}</span>}
                  </div>
                </>
              )}

              {/* Campos adicionais para CNPJ */}
              {formData.tipo_cliente === 'cnpj' && (
                <>
                  <div className="form-group">
                    <label>CPF *</label>
                    <input
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleChange}
                      placeholder="000.000.000-00"
                      className={errors.cpf ? 'error' : ''}
                    />
                    {errors.cpf && <span className="error-text">{errors.cpf}</span>}
                  </div>

                  <div className="form-group">
                    <label>Nome do Representante *</label>
                    <input
                      type="text"
                      name="nome_representante"
                      value={formData.nome_representante}
                      onChange={handleChange}
                      placeholder="Nome completo do representante legal"
                      className={errors.nome_representante ? 'error' : ''}
                      disabled={buscandoCpf}
                    />
                    {errors.nome_representante && <span className="error-text">{errors.nome_representante}</span>}
                  </div>

                  <div className="form-group">
                    <label>RG *</label>
                    <input
                      type="text"
                      name="rg"
                      value={formData.rg}
                      onChange={handleChange}
                      placeholder="00.000.000-0"
                      className={errors.rg ? 'error' : ''}
                    />
                    {errors.rg && <span className="error-text">{errors.rg}</span>}
                  </div>

                  <div className="form-group">
                    <label>Nome da Mãe *</label>
                    <input
                      type="text"
                      name="nome_mae"
                      value={formData.nome_mae}
                      onChange={handleChange}
                      placeholder="Nome completo da mãe"
                      className={errors.nome_mae ? 'error' : ''}
                      disabled={buscandoCpf}
                    />
                    {errors.nome_mae && <span className="error-text">{errors.nome_mae}</span>}
                  </div>

                  <div className="form-group">
                    <label>Data de Nascimento *</label>
                    <input
                      type="date"
                      name="data_nascimento"
                      value={formData.data_nascimento}
                      onChange={handleChange}
                      className={errors.data_nascimento ? 'error' : ''}
                      disabled={buscandoCpf}
                    />
                    {errors.data_nascimento && <span className="error-text">{errors.data_nascimento}</span>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Endereço */}
          <div className="form-section">
            <h2>📍 Endereço</h2>
            <div className="form-grid">
              <div className="form-group cep-group">
                <label>CEP *</label>
                <div className="cep-input-wrapper">
                  <input
                    type="text"
                    name="cep"
                    value={formData.cep}
                    onChange={handleChange}
                    placeholder="00000-000"
                    className={errors.cep ? 'error' : ''}
                  />
                  <button
                    type="button"
                    onClick={buscarCep}
                    className="btn-buscar-cep"
                    disabled={buscandoCep}
                  >
                    {buscandoCep ? '⏳' : '🔍'} Buscar
                  </button>
                </div>
                {errors.cep && <span className="error-text">{errors.cep}</span>}
              </div>

              <div className="form-group">
                <label>Logradouro *</label>
                <input
                  type="text"
                  name="logradouro"
                  value={formData.logradouro}
                  onChange={handleChange}
                  placeholder="Rua, Avenida, etc."
                  className={errors.logradouro ? 'error' : ''}
                />
                {errors.logradouro && <span className="error-text">{errors.logradouro}</span>}
              </div>

              <div className="form-group">
                <label>Número *</label>
                <input
                  type="text"
                  name="numero"
                  value={formData.numero}
                  onChange={handleChange}
                  placeholder="123"
                  className={errors.numero ? 'error' : ''}
                />
                {errors.numero && <span className="error-text">{errors.numero}</span>}
              </div>

              <div className="form-group">
                <label>Bairro *</label>
                <input
                  type="text"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleChange}
                  placeholder="Nome do bairro"
                  className={errors.bairro ? 'error' : ''}
                />
                {errors.bairro && <span className="error-text">{errors.bairro}</span>}
              </div>

              <div className="form-group">
                <label>Cidade *</label>
                <input
                  type="text"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  placeholder="Nome da cidade"
                  className={errors.cidade ? 'error' : ''}
                />
                {errors.cidade && <span className="error-text">{errors.cidade}</span>}
              </div>

              <div className="form-group">
                <label>Estado (UF) *</label>
                <input
                  type="text"
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  placeholder="SP"
                  maxLength="2"
                  className={errors.estado ? 'error' : ''}
                />
                {errors.estado && <span className="error-text">{errors.estado}</span>}
              </div>

              <div className="form-group">
                <label>Estado Completo *</label>
                <input
                  type="text"
                  name="estado_completo"
                  value={formData.estado_completo}
                  onChange={handleChange}
                  placeholder="São Paulo"
                  className={errors.estado_completo ? 'error' : ''}
                />
                {errors.estado_completo && <span className="error-text">{errors.estado_completo}</span>}
              </div>

              <div className="form-group">
                <label>Complemento 1 (opcional)</label>
                <input
                  type="text"
                  name="complemento1"
                  value={formData.complemento1}
                  onChange={handleChange}
                  placeholder="Apto, Bloco, etc."
                />
              </div>

              <div className="form-group">
                <label>Complemento 2 (opcional)</label>
                <input
                  type="text"
                  name="complemento2"
                  value={formData.complemento2}
                  onChange={handleChange}
                  placeholder="Ponto de referência"
                />
              </div>
            </div>
          </div>

          {/* Agendamento de Instalação */}
          <div className="form-section">
            <h2>📅 Agendamento de Instalação</h2>
            <p className="section-subtitle">Marque a data e o período sugeridos pelo cliente para a instalação</p>
            
            <div className="agendamento-section">
              <h3>Opção 1 *</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Data Sugerida *</label>
                  <input
                    type="date"
                    name="data_agendamento1"
                    value={formData.data_agendamento1}
                    onChange={handleChange}
                    className={errors.data_agendamento1 ? 'error' : ''}
                  />
                  {errors.data_agendamento1 && <span className="error-text">{errors.data_agendamento1}</span>}
                </div>

                <div className="form-group">
                  <label>Período *</label>
                  <select
                    name="periodo_agendamento1"
                    value={formData.periodo_agendamento1}
                    onChange={handleChange}
                  >
                    <option value="manha">Manhã</option>
                    <option value="tarde">Tarde</option>
                    <option value="noite">Noite</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="agendamento-section">
              <h3>Opção 2 (opcional)</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Data Sugerida</label>
                  <input
                    type="date"
                    name="data_agendamento2"
                    value={formData.data_agendamento2}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Período</label>
                  <select
                    name="periodo_agendamento2"
                    value={formData.periodo_agendamento2}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="manha">Manhã</option>
                    <option value="tarde">Tarde</option>
                    <option value="noite">Noite</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="aceita_antecipacao"
                  checked={formData.aceita_antecipacao}
                  onChange={handleChange}
                />
                Cliente Aceita Antecipação
              </label>
            </div>

            <div className="form-group">
              <label>Observação sobre Instalação (opcional)</label>
              <textarea
                name="observacao_instalacao"
                value={formData.observacao_instalacao}
                onChange={handleChange}
                placeholder="Digite observações adicionais sobre a instalação..."
                rows="3"
              />
            </div>
          </div>

          {/* Dados Financeiros */}
          <div className="form-section">
            <h2>💰 Informações Financeiras</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Data de Vencimento *</label>
                <select
                  name="data_vencimento"
                  value={formData.data_vencimento}
                  onChange={handleChange}
                >
                  <option value={1}>Dia 1</option>
                  <option value={10}>Dia 10</option>
                  <option value={17}>Dia 17</option>
                  <option value={21}>Dia 21</option>
                  <option value={26}>Dia 26</option>
                </select>
              </div>

              <div className="form-group">
                <label>Valor Total (R$) *</label>
                <input
                  type="text"
                  name="valor_total"
                  value={formData.valor_total}
                  onChange={handleChange}
                  placeholder="0,00"
                  className={errors.valor_total ? 'error' : ''}
                />
                {errors.valor_total && <span className="error-text">{errors.valor_total}</span>}
              </div>
            </div>
          </div>

          {/* Observações */}
          <div className="form-section">
            <h2>📝 Observação da Venda</h2>
            <div className="form-group">
              <label>Observação Geral (opcional)</label>
              <textarea
                ref={observacaoVendaRef}
                name="observacao_venda"
                value={formData.observacao_venda}
                onChange={handleChange}
                placeholder="Digite observações gerais sobre esta venda, descreva o pacote ofertado ao cliente detalhadamente. COLOCAR O PACOTE (ex: 600 MEGA, 700 MEGA, 1 GIGA...)"
                rows="4"
              />
            </div>
          </div>

          {/* Documentos Anexos */}
          <div className="form-section">
            <h2>📎 Documentos Anexos (Opcional)</h2>
            <div className="form-group">
              <label>Arquivos (JPEG, JPG, PNG ou PDF - máximo 10MB cada, até 5 arquivos)</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/jpg,image/png,application/pdf"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  
                  // Validar quantidade máxima (5 arquivos)
                  if (documentosAnexos.length + files.length > 5) {
                    alert('❌ Você pode anexar no máximo 5 arquivos por venda');
                    e.target.value = '';
                    return;
                  }
                  
                  // Validar cada arquivo
                  const validFiles = [];
                  for (const file of files) {
                    // Validar tamanho (10MB)
                    if (file.size > 10 * 1024 * 1024) {
                      alert(`❌ O arquivo "${file.name}" deve ter no máximo 10MB`);
                      continue;
                    }
                    // Validar tipo - apenas JPEG, JPG, PNG ou PDF
                    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
                    if (!validTypes.includes(file.type)) {
                      alert(`❌ O arquivo "${file.name}" deve ser uma imagem (JPEG, JPG, PNG) ou PDF`);
                      continue;
                    }
                    validFiles.push(file);
                  }
                  
                  if (validFiles.length > 0) {
                    setDocumentosAnexos(prev => [...prev, ...validFiles]);
                  }
                  
                  e.target.value = '';
                }}
                disabled={documentosAnexos.length >= 5}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'var(--cinza-escuro-2)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  color: 'var(--texto-primario)',
                  cursor: documentosAnexos.length >= 5 ? 'not-allowed' : 'pointer',
                  opacity: documentosAnexos.length >= 5 ? 0.6 : 1
                }}
              />
              {documentosAnexos.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ 
                    marginBottom: '0.5rem', 
                    color: 'var(--texto-primario)', 
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    Arquivos anexados ({documentosAnexos.length}/5):
                  </div>
                  {documentosAnexos.map((file, index) => (
                    <div 
                      key={index}
                      style={{ 
                        marginTop: '0.5rem', 
                        padding: '0.75rem', 
                        background: 'var(--cinza-escuro-2)', 
                        borderRadius: '6px', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center' 
                      }}
                    >
                      <span style={{ color: 'var(--texto-primario)', flex: 1 }}>
                        📄 {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setDocumentosAnexos(prev => prev.filter((_, i) => i !== index));
                        }}
                        style={{
                          background: '#d32f2f',
                          color: 'white',
                          border: 'none',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          marginLeft: '0.5rem'
                        }}
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {documentosAnexos.length >= 5 && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  color: '#ff9800', 
                  fontSize: '0.875rem' 
                }}>
                  ⚠️ Limite de 5 arquivos atingido. Remova um arquivo para adicionar outro.
                </div>
              )}
            </div>
          </div>

          {/* Botões */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/vendas')}
              className="btn-secondary"
              disabled={loading}
            >
              ❌ Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? '⏳ Salvando...' : '✅ Cadastrar Venda'}
            </button>
          </div>
        </form>

        {/* Modal: Já colocou o pacote na observação? */}
        {showModalPacote && (
          <div className="modal-overlay" onClick={() => setShowModalPacote(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h2>📝 Confirmação</h2>
                <span className="btn-close" onClick={() => setShowModalPacote(false)} aria-label="Fechar">×</span>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: '1.5rem', fontSize: '1rem', lineHeight: 1.5 }}>
                  JÁ COLOCOU O PACOTE NA OBSERVAÇÃO? O PACOTE É OBRIGATÓRIO ESCREVER NA OBSERVAÇÃO.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowModalPacote(false);
                      setTimeout(() => {
                        observacaoVendaRef.current?.scrollIntoView({ behavior: 'smooth' });
                        observacaoVendaRef.current?.focus();
                      }, 100);
                    }}
                  >
                    NÃO
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={loading}
                    onClick={() => {
                      setShowModalPacote(false);
                      confirmarEnvioVenda();
                    }}
                  >
                    {loading ? '⏳ Salvando...' : 'SIM'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NovaVenda;
